import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ACTIVE_ORDER_STATUSES,
  OrderStatus,
  type OrderType,
  type ParcelApproval,
  type ParcelPayer,
  type ParcelSize,
} from '@kick/types';
import type {
  CustomerAddress,
  LatLng,
  PaymentMethod,
  Product,
  ProductOptionGroup,
} from '@kick/types';
import { api } from './api';

/** Comercio tal como lo devuelve la tienda pública. */
export interface ComercioPublico {
  id: string;
  name: string;
  slug: string;
  categoryId: string;
  categoryName: string;
  description: string | null;
  logoUrl: string | null;
  coverUrl: string | null;
  addressLine: string;
  city: string;
  location: LatLng;
  avgPrepMinutes: number;
  minOrderAmount: number;
  /** Pedido por lista: sin catálogo; el cliente escribe y el comercio cotiza. */
  listOrdering: boolean;
  ratingAvg: number;
  ratingCount: number;
  isOpen: boolean;
  distanceMeters?: number;
}

export interface ProductoPublico extends Product {
  optionGroups: ProductOptionGroup[];
}

export interface DetalleComercio extends ComercioPublico {
  phone: string | null;
  schedules: { weekday: number; opensAt: string; closesAt: string; isClosed: boolean }[];
  menu: { id: string; name: string; sortOrder: number; products: ProductoPublico[] }[];
}

export interface CategoriaPlataforma {
  id: string;
  name: string;
  slug: string;
  icon: string | null;
  imageUrl: string | null;
  merchantCount: number;
}

export interface Zona {
  id: string;
  name: string;
  baseFee: number;
  perKmFee: number;
  freeDeliveryOver: number | null;
}

export interface FiltrosComercios {
  lat?: number;
  lng?: number;
  radius?: number;
  categoryId?: string;
  minRating?: number;
  sort?: 'distance' | 'rating' | 'delivery_fee' | 'popular';
  q?: string;
}

export const claves = {
  categorias: () => ['categorias'] as const,
  comercios: (filtros: FiltrosComercios) => ['comercios', filtros] as const,
  comercio: (slug: string) => ['comercio', slug] as const,
  buscar: (q: string) => ['buscar', q] as const,
  zona: (punto: LatLng | null) => ['zona', punto?.lat, punto?.lng] as const,
  direcciones: () => ['direcciones'] as const,
};

// ── Catálogo público ──────────────────────────────────────

export function useCategorias() {
  return useQuery({
    queryKey: claves.categorias(),
    queryFn: async () => (await api.get<CategoriaPlataforma[]>('/public/categorias')).data,
    // Las categorías casi no cambian.
    staleTime: 30 * 60 * 1000,
  });
}

export function useComercios(filtros: FiltrosComercios) {
  return useQuery({
    queryKey: claves.comercios(filtros),
    queryFn: async () => {
      const params = Object.fromEntries(
        Object.entries(filtros).filter(([, v]) => v !== undefined && v !== ''),
      );
      return (await api.get<ComercioPublico[]>('/public/comercios', { params })).data;
    },
    // Si un comercio abre o cierra, debe notarse pronto.
    staleTime: 60 * 1000,
  });
}

export function useComercio(slug: string, punto?: LatLng | null) {
  return useQuery({
    queryKey: [...claves.comercio(slug), punto?.lat, punto?.lng],
    queryFn: async () =>
      (
        await api.get<DetalleComercio>(`/public/comercios/${slug}`, {
          params: punto ? { lat: punto.lat, lng: punto.lng } : undefined,
        })
      ).data,
    enabled: !!slug,
  });
}

export interface ResultadoBusqueda {
  merchants: {
    id: string;
    name: string;
    slug: string;
    logoUrl: string | null;
    categoryName: string;
    ratingAvg: number;
    isOpen: boolean;
    distanceMeters?: number;
  }[];
  products: {
    id: string;
    name: string;
    description: string | null;
    price: number;
    imageUrl: string | null;
    isAvailable: boolean;
    merchantId: string;
    merchantName: string;
    merchantSlug: string;
    merchantIsOpen: boolean;
  }[];
  total: number;
}

export function useBusqueda(q: string, punto?: LatLng | null) {
  return useQuery({
    queryKey: [...claves.buscar(q), punto?.lat, punto?.lng],
    queryFn: async () =>
      (
        await api.get<ResultadoBusqueda>('/public/buscar', {
          params: { q, ...(punto ? { lat: punto.lat, lng: punto.lng } : {}) },
        })
      ).data,
    // Menos de 2 caracteres no aporta nada y el servidor lo rechaza.
    enabled: q.trim().length >= 2,
  });
}

export function useZona(punto: LatLng | null) {
  return useQuery({
    queryKey: claves.zona(punto),
    queryFn: async () =>
      (
        await api.get<{ cubierto: boolean; zona: Zona | null }>('/public/zona', {
          params: { lat: punto!.lat, lng: punto!.lng },
        })
      ).data,
    enabled: !!punto,
    staleTime: 10 * 60 * 1000,
  });
}

// ── Direcciones del cliente ───────────────────────────────

export function useDirecciones(habilitado: boolean) {
  return useQuery({
    queryKey: claves.direcciones(),
    queryFn: async () => (await api.get<CustomerAddress[]>('/me/direcciones')).data,
    enabled: habilitado,
  });
}

export function useGuardarDireccion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...datos }: { id?: string } & Record<string, unknown>) => {
      const { data } = id
        ? await api.patch<CustomerAddress>(`/me/direcciones/${id}`, datos)
        : await api.post<CustomerAddress>('/me/direcciones', datos);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: claves.direcciones() }),
  });
}

export function useEliminarDireccion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/me/direcciones/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: claves.direcciones() }),
  });
}

// ── Cálculo de envío ──────────────────────────────────────

/** Distancia en metros entre dos puntos (haversine). */
export function distanciaMetros(a: LatLng, b: LatLng): number {
  const R = 6_371_000;
  const rad = (g: number): number => (g * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return Math.round(2 * R * Math.asin(Math.sqrt(h)));
}

/**
 * Costo de envío estimado.
 *
 * Es solo una estimación para la vitrina: el importe que se cobra lo calcula
 * el servidor al crear el pedido, con la ruta real y la zona de destino.
 */
export function estimarEnvio(
  zona: Zona | null,
  metros: number,
  subtotal: number,
): { costo: number; gratis: boolean } {
  if (!zona) return { costo: 0, gratis: false };

  if (zona.freeDeliveryOver !== null && subtotal >= zona.freeDeliveryOver) {
    return { costo: 0, gratis: true };
  }

  const bruto = zona.baseFee + Math.round((metros / 1000) * zona.perKmFee);
  // A centenas de peso: nadie cobra $4.387 de domicilio.
  return { costo: Math.round(bruto / 10_000) * 10_000, gratis: false };
}

// ── Cotización del domicilio (servidor) ───────────────────

export interface CotizacionEnvio {
  /** Distancia real entre el punto de recogida del comercio y el destino. */
  distanceMeters: number;
  zone: { id: string; name: string } | null;
  /** Costo en centavos. */
  fee: number;
  free: boolean;
  /** Cuánto falta de subtotal para que el envío sea gratis. */
  freeDeliveryIn: number | null;
  covered: boolean;
}

/**
 * Cotiza el domicilio en el servidor.
 *
 * La distancia la mide PostGIS entre la ubicación del comercio y la dirección
 * de entrega. El navegador no calcula el precio: solo lo muestra.
 */
export function useCotizacionEnvio(
  merchantId: string | undefined,
  destino: LatLng | null,
  subtotal: number,
) {
  return useQuery({
    queryKey: ['cotizacion', merchantId, destino?.lat, destino?.lng, subtotal],
    queryFn: async () =>
      (
        await api.post<CotizacionEnvio>('/zonas/cotizar', {
          merchantId,
          destination: destino,
          subtotal,
        })
      ).data,
    enabled: !!merchantId && !!destino,
    // El costo depende del subtotal: se recalcula al cambiar el carrito.
    staleTime: 30 * 1000,
  });
}

// ── Pedidos ───────────────────────────────────────────────

export interface LineaPedido {
  id: string;
  productId: string | null;
  productName: string;
  unitPrice: number;
  quantity: number;
  options: { groupName: string; optionName: string; priceDelta: number }[];
  notes: string | null;
  lineTotal: number;
}

export interface Pedido {
  id: string;
  code: string;
  /** De un comercio, o de un punto a otro. */
  type: OrderType;
  /** Nulo en una encomienda: no hay comercio. */
  merchantId: string | null;
  status: OrderStatus;
  subtotal: number;
  deliveryFee: number;
  serviceFee: number;
  /** Tarifa de lluvia de una encomienda (en un pedido de comercio va en serviceFee). */
  rainFee: number;
  discountTotal: number;
  tip: number;
  total: number;
  paymentMethod: PaymentMethod;
  paymentStatus: string;
  deliveryAddress: {
    label: string;
    addressLine: string;
    details: string | null;
    city: string;
  };
  distanceMeters: number;
  customerNotes: string | null;
  cancelReason: string | null;
  estimatedDeliveryAt: string | null;
  placedAt: string | null;
  pickedUpAt: string | null;
  deliveredAt: string | null;
  createdAt: string;
  /** De dónde se recoge, sea comercio o encomienda. */
  pickupName: string;
  pickupAddress: {
    label: string;
    addressLine: string;
    details: string | null;
    city: string;
  };
  /** Los tres nulos en una encomienda: la pantalla usa `pickupName`. */
  merchantName: string | null;
  merchantSlug: string | null;
  merchantLogo: string | null;
}

export interface DomiciliarioPedido {
  id: string;
  fullName: string;
  avatarUrl: string | null;
  vehicleType: string;
  plate: string | null;
  ratingAvg: number;
  /** Enmascarado: el cliente identifica la llamada, no el número. */
  maskedPhone: string;
}

export interface PedidoDetalle extends Pedido {
  /** Pedido por lista: en qué va la revisión; nulo en un pedido normal. */
  listStage: string | null;
  budgetCap: number | null;
  courier: DomiciliarioPedido | null;
  /** Solo en encomiendas: qué se manda y a quién. */
  parcel: {
    size: ParcelSize;
    description: string;
    declaredValue: number | null;
    payer: ParcelPayer;
    recipientName: string;
    recipientPhone: string;
    pickupPhotoUrl: string | null;
    deliveryPhotoUrl: string | null;
    /** Los cuatro nulos salvo en las programadas. */
    scheduledFor: string | null;
    approval: ParcelApproval | null;
    decisionNote: string | null;
    quotedTotal: number | null;
    repricedTotal: number | null;
  } | null;
  items: LineaPedido[];
  history: {
    id: string;
    fromStatus: OrderStatus | null;
    toStatus: OrderStatus;
    reason: string | null;
    createdAt: string;
  }[];
}

export interface CotizacionPedido {
  subtotal: number;
  deliveryFee: number;
  serviceFee: number;
  discountTotal: number;
  tip: number;
  total: number;
  distanceMeters: number;
  zoneName: string | null;
  covered: boolean;
  minOrderAmount: number;
  meetsMinimum: boolean;
  coupon: { code: string; description: string | null; discount: number } | null;
  /** Descuento de la insignia premium; cero para quien no la tiene. */
  premiumDiscount: number;
  premiumPercent: number;
}

/** Desglose exacto de lo que se va a cobrar, calculado por el servidor. */
export function useCotizarPedido(datos: {
  merchantId?: string;
  addressId?: string;
  items: { productId: string; quantity: number; optionIds: string[]; notes?: string }[];
  tip: number;
  couponCode?: string;
}) {
  return useQuery({
    queryKey: ['cotizar-pedido', datos],
    queryFn: async () => (await api.post<CotizacionPedido>('/pedidos/cotizar', datos)).data,
    enabled: !!datos.merchantId && !!datos.addressId && datos.items.length > 0,
    retry: false,
  });
}

export function useCrearPedido() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (datos: Record<string, unknown>) =>
      (await api.post<Pedido>('/pedidos', datos)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mis-pedidos'] }),
  });
}

export function useMisPedidos() {
  return useQuery({
    queryKey: ['mis-pedidos'],
    queryFn: async () =>
      (await api.get<{ data: Pedido[]; hasMore: boolean; nextCursor: string | null }>(
        '/pedidos/mis-pedidos?limit=20',
      )).data,
  });
}

export function usePedido(id: string | undefined, enVivo = false) {
  return useQuery({
    queryKey: ['pedido', id],
    queryFn: async () => (await api.get<PedidoDetalle>(`/pedidos/${id}`)).data,
    enabled: !!id,
    /**
     * Con el canal en vivo abierto no hace falta preguntar: los eventos traen
     * cada cambio. La consulta por reloj queda como red de seguridad para
     * cuando el socket está caído.
     */
    refetchInterval: (consulta) => {
      if (enVivo) return false;
      const estado = consulta.state.data?.status;
      return estado && ACTIVE_ORDER_STATUSES.includes(estado) ? 15_000 : false;
    },
  });
}

// ── Pago por transferencia ────────────────────────────────

export interface CuentaDomiciliario {
  id: string;
  type: string;
  bankName: string | null;
  accountKind: string | null;
  accountNumber: string;
  holderName: string;
  holderDocument: string | null;
  qrUrl: string | null;
  isDefault: boolean;
}

export interface Comprobante {
  id: string;
  imageUrl: string;
  reference: string | null;
  declaredAmount: number | null;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  rejectReason: string | null;
  reviewedAt: string | null;
  createdAt: string;
}

export interface PagoPedido {
  orderId: string;
  paymentMethod: PaymentMethod;
  paymentStatus: string;
  total: number;
  accounts: CuentaDomiciliario[];
  receipts: Comprobante[];
  /** Qué le toca hacer al cliente ahora, resuelto en el servidor. */
  pendiente: 'NADA' | 'ESPERAR_DOMICILIARIO' | 'TRANSFERIR' | 'EN_REVISION';
}

export function usePagoPedido(orderId: string | undefined, habilitado = true) {
  return useQuery({
    queryKey: ['pago', orderId],
    queryFn: async () => (await api.get<PagoPedido>(`/pagos/pedidos/${orderId}`)).data,
    enabled: !!orderId && habilitado,
  });
}

export function useSubirComprobante(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      archivo,
      accountId,
      reference,
      declaredAmount,
    }: {
      archivo: File;
      accountId?: string;
      reference?: string;
      declaredAmount?: number;
    }) => {
      const datos = new FormData();
      datos.append('imagen', archivo);
      if (accountId) datos.append('accountId', accountId);
      if (reference) datos.append('reference', reference);
      if (declaredAmount) datos.append('declaredAmount', String(declaredAmount));

      const { data } = await api.post<Comprobante>(
        `/pagos/pedidos/${orderId}/comprobante`,
        datos,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pago', orderId] });
      void qc.invalidateQueries({ queryKey: ['pedido', orderId] });
    },
  });
}

// ── Rastreo en el mapa ────────────────────────────────────

export interface PuntoTraza extends LatLng {
  recordedAt: string;
}

export interface Rastreo {
  orderId: string;
  code: string;
  status: OrderStatus;
  /** El origen del recorrido: el comercio, o el punto de recogida de la encomienda. */
  pickup: { name: string; addressLine: string; location: LatLng };
  destination: { addressLine: string; location: LatLng };
  courier: DomiciliarioPedido | null;
  courierLocation: PuntoTraza | null;
  trace: PuntoTraza[];
  /** El trazado por las calles entre recogida y entrega; null si no se pudo calcular. */
  route: LatLng[] | null;
  distanceMeters: number;
  estimatedDeliveryAt: string | null;
  pickedUpAt: string | null;
  deliveredAt: string | null;
}

/**
 * Datos del mapa de seguimiento.
 *
 * La posición llega por el canal en vivo; el sondeo cada diez segundos es la
 * red de seguridad. En el teléfono, el navegador suspende el socket al pasar
 * a segundo plano y puede perder eventos al volver: sin el sondeo, la moto se
 * quedaba clavada donde estaba y había que salir y volver a entrar. Se sigue
 * sondeando aunque la pestaña no tenga el foco, y para cuando el pedido
 * termina.
 */
export function useRastreo(id: string | undefined) {
  return useQuery({
    queryKey: ['rastreo', id],
    queryFn: async () => (await api.get<Rastreo>(`/pedidos/${id}/rastreo`)).data,
    enabled: !!id,
    staleTime: 5 * 1000,
    refetchInterval: (consulta) => {
      const estado = consulta.state.data?.status;
      const terminado =
        estado === OrderStatus.DELIVERED ||
        estado === OrderStatus.CANCELLED ||
        estado === OrderStatus.REJECTED;
      return terminado ? false : 10_000;
    },
    refetchIntervalInBackground: true,
  });
}

export function useCancelarPedido() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) =>
      (await api.patch<Pedido>(`/pedidos/${id}/cancelar`, { reason })).data,
    onSuccess: (pedido) => {
      void qc.invalidateQueries({ queryKey: ['pedido', pedido.id] });
      void qc.invalidateQueries({ queryKey: ['mis-pedidos'] });
    },
  });
}

// ── Encomiendas ───────────────────────────────────────────

export interface CotizacionEncomienda {
  distanceMeters: number;
  deliveryFee: number;
  sizeFee: number;
  /** Tarifa de lluvia, si está encendida; ya va dentro de `total`. */
  rainFee: number;
  total: number;
  zoneName: string | null;
  covered: boolean;
  estimatedMinutes: number;
}

/**
 * El precio de la encomienda, recalculado a medida que se mueven los puntos.
 *
 * Va por `useQuery` y no por una mutación porque es una lectura pura y se
 * repite: cada vez que alguien arrastra el marcador vuelve a pedirse. Con la
 * clave puesta en los dos puntos y el tamaño, React Query cachea el resultado
 * y volver atrás en el formulario no genera otra llamada.
 */
export function useCotizarEncomienda(datos: {
  origin: LatLng | null;
  destination: LatLng | null;
  size: ParcelSize;
}) {
  const listo = !!datos.origin && !!datos.destination;

  return useQuery({
    queryKey: [
      'encomienda-cotizacion',
      datos.origin?.lat,
      datos.origin?.lng,
      datos.destination?.lat,
      datos.destination?.lng,
      datos.size,
    ],
    queryFn: async () =>
      (
        await api.post<CotizacionEncomienda>('/encomiendas/cotizar', {
          origin: datos.origin,
          destination: datos.destination,
          size: datos.size,
        })
      ).data,
    enabled: listo,
    staleTime: 60 * 1000,
  });
}

export interface CotizacionNoAliado extends CotizacionEncomienda {
  /** El recargo por comercio no aliado, que fija administración. */
  nonPartnerFee: number;
}

/** El precio de pedir a un comercio no aliado: domicilio por distancia más el recargo. */
export function useCotizarNoAliado(origin: LatLng | null, destination: LatLng | null) {
  const listo = !!origin && !!destination;
  return useQuery({
    queryKey: ['no-aliado-cotizacion', origin?.lat, origin?.lng, destination?.lat, destination?.lng],
    queryFn: async () =>
      (await api.post<CotizacionNoAliado>('/encomiendas/no-aliado/cotizar', { origin, destination })).data,
    enabled: listo,
    staleTime: 60_000,
  });
}

/** Pedir algo de un comercio que no está en la plataforma: lo asigna administración a mano. */
export function useCrearNoAliado() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (datos: Record<string, unknown>) =>
      (await api.post<{ id: string; code: string }>('/encomiendas/no-aliado', datos)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mis-pedidos'] }),
  });
}

export function useCrearEncomienda() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (datos: Record<string, unknown>) =>
      (await api.post<{ id: string; code: string }>('/encomiendas', datos)).data,
    // La encomienda entra al mismo historial que los domicilios.
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mis-pedidos'] }),
  });
}

/**
 * El cliente confirma el precio nuevo de una encomienda programada.
 *
 * Solo aparece cuando la tarifa subió por encima del umbral entre la reserva y
 * el despacho. Al confirmarlo sale de inmediato; si no contesta a tiempo, se
 * cancela sola.
 */
export function useAceptarPrecioEncomienda(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.post(`/encomiendas/${orderId}/aceptar-precio`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pedido', orderId] }),
  });
}

// ── Insignia premium ──────────────────────────────────────

export interface EstadoPremium {
  premium: boolean;
  discountPercent: number;
  threshold: number;
  ordersThisMonth: number;
  ordersLastMonth: number;
  remaining: number;
}

/**
 * Mi insignia: si la tengo y cuánto me falta.
 *
 * Se consulta al abrir la cuenta, no en cada página: el estado cambia con las
 * entregas, no con la navegación.
 */
export function useMiPremium(activo: boolean) {
  return useQuery({
    queryKey: ['mi-premium'],
    queryFn: async () => (await api.get<EstadoPremium>('/me/premium')).data,
    enabled: activo,
    staleTime: 5 * 60 * 1000,
  });
}

// ── Pedido por lista ────────────────────────────────────────

export interface ItemLista {
  id: string;
  position: number;
  name: string;
  quantity: number;
  unit: string;
  note: string | null;
  allowSubstitute: boolean;
  status: 'PENDING' | 'CONFIRMED' | 'UNAVAILABLE';
  price: number | null;
  suggestionName: string | null;
  suggestionPrice: number | null;
  suggestionDecision: 'ACCEPTED' | 'REJECTED' | null;
}

export interface ListaDelPedido {
  orderId: string;
  code: string;
  stage: 'QUOTING' | 'AWAITING_APPROVAL' | 'APPROVED';
  status: OrderStatus;
  budgetCap: number | null;
  subtotal: number;
  deliveryFee: number;
  serviceFee: number;
  total: number;
  pendientes: number;
  sinDecidir: number;
  items: ItemLista[];
}

export function useListaDelPedido(orderId: string | undefined, activo: boolean) {
  return useQuery({
    queryKey: ['pedido-lista', orderId],
    queryFn: async () => (await api.get<ListaDelPedido>(`/pedidos/${orderId}/lista`)).data,
    enabled: !!orderId && activo,
    // El socket trae cada cambio; esto es la red de seguridad. Sigue
    // sondeando aunque la ventana no tenga el foco: el cliente suele estar
    // en otra aplicación mientras espera la cotización.
    refetchInterval: 7_000,
    refetchIntervalInBackground: true,
  });
}

export function useCrearPedidoLista() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (datos: Record<string, unknown>) =>
      (await api.post<{ id: string; code: string }>('/pedidos/lista', datos)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mis-pedidos'] }),
  });
}

export function useDecidirSugerencia(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ itemId, accept }: { itemId: string; accept: boolean }) =>
      (await api.post(`/pedidos/${orderId}/lista/${itemId}/decision`, { accept })).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pedido-lista', orderId] });
      void qc.invalidateQueries({ queryKey: ['pedido', orderId] });
    },
  });
}

export function useAprobarLista(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await api.post(`/pedidos/${orderId}/lista/aprobar`)).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pedido-lista', orderId] });
      void qc.invalidateQueries({ queryKey: ['pedido', orderId] });
    },
  });
}

/** El autocompletar: lo que este comercio ya cotizó, con su último precio. */
export function useSugerenciasLista(slug: string, q: string) {
  return useQuery({
    queryKey: ['lista-sugerencias', slug, q],
    queryFn: async () =>
      (
        await api.get<{ name: string; unit: string; lastPrice: number }[]>(
          `/public/comercios/${slug}/lista/sugerencias`,
          { params: { q } },
        )
      ).data,
    enabled: q.trim().length >= 2,
    staleTime: 30_000,
  });
}

export function useUltimaLista(merchantId: string | null) {
  return useQuery({
    queryKey: ['lista-ultima', merchantId],
    queryFn: async () =>
      (
        await api.get<
          { name: string; quantity: number; unit: string; note: string | null; allowSubstitute: boolean }[]
        >('/me/listas/ultima', { params: { merchantId } })
      ).data,
    enabled: !!merchantId,
  });
}

export interface ListaGuardada {
  id: string;
  merchantId: string;
  name: string;
  items: { name: string; quantity: number; unit: string; note?: string; allowSubstitute?: boolean }[];
}

export function useListasGuardadas(merchantId: string | null) {
  return useQuery({
    queryKey: ['listas-guardadas', merchantId],
    queryFn: async () =>
      (await api.get<ListaGuardada[]>('/me/listas', { params: { merchantId } })).data,
    enabled: !!merchantId,
  });
}

export function useGuardarLista() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (datos: Record<string, unknown>) =>
      (await api.post<ListaGuardada>('/me/listas', datos)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['listas-guardadas'] }),
  });
}

export function useEliminarListaGuardada() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.delete(`/me/listas/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['listas-guardadas'] }),
  });
}
